import { gql } from 'graphql-request';
import { useCustomerIdToken } from '@hooks/customer/useCustomer';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import useSWRImmutable from 'swr/immutable';
import { GraphQLRequestWithTokenProps, requestWithTokenRequired } from '@utils/graphql-request';
import type { MediaType, MetaProps } from '@repo/ui/src/types';

export interface SavedRecipeItem {
  contentId: string;
  savedAtDate: string;
  content: {
    _meta: MetaProps;
    assets: Array<MediaType>;
    recipeMetaSection: {
      title: string;
      description?: string;
      cookTime?: number;
      prepTime?: number;
      complexity?: string;
      hardwood?: string;
    };
    ratingSummary?: {
      average: number;
      count: number;
    };
  } | null;
}

interface SavedRecipesData {
  getSelf: {
    savedContent: {
      hits: Array<SavedRecipeItem>;
      nextToken: string | null;
    };
  };
}

const SAVED_RECIPES_QUERY = gql`
  query GetSavedRecipes($limit: Int) {
    getSelf {
      savedContent(limit: $limit) {
        hits {
          contentId
          savedAtDate
          content {
            ... on RecipeListItem {
              _meta {
                deliveryId
                deliveryKey
              }
              assets {
                endpoint
                name
                defaultHost
              }
              recipeMetaSection {
                title
                description
                cookTime
                prepTime
                complexity
                hardwood
              }
              ratingSummary {
                average
                count
              }
            }
          }
        }
        nextToken
      }
    }
  }
`;

const fetcher = async (props: GraphQLRequestWithTokenProps<{ limit: number }>) => {
  const data = await requestWithTokenRequired<SavedRecipesData>(props);
  return data?.getSelf?.savedContent?.hits?.filter((hit) => hit.content !== null) ?? [];
};

export const useSavedRecipes = () => {
  const { locale } = useCurrentUrl();
  const idToken = useCustomerIdToken();

  return useSWRImmutable(
    idToken ? { document: SAVED_RECIPES_QUERY, variables: { limit: 100 }, locale, idToken } : null,
    fetcher,
    { shouldRetryOnError: false },
  );
};
